// Tutor preview of a Skill Builder, before assigning it.
//
// Renders the REAL AcroForm fields rather than a rasterised image, the same way
// the interactive worksheet preview does. That distinction is the point: a
// worksheet whose blanks were never authored as form fields looks identical to a
// working one until someone tries to click into them, and the tutor is the last
// person who can catch that before it reaches a child.
//
// Nothing typed in here is saved.

import { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"
import {
  getBmdSkillbuilderPreview,
  getBmdSkillbuilderPdf,
} from "../services/bmd-skillbuilder.service"
import type { BmdSkillbuilderPreviewResponse } from "../types/bmd-skillbuilder.types"

// react-pdf 7.7.x bundles its own pdfjs - the worker must be the SAME version.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

type Props = {
  skillbuilderId: number | null
  skillName?: string
  /**
   * Set when previewing something ASSIGNED to a student, as on the lesson plan.
   * The preview then shows THE STUDENT'S ATTEMPT: their answers, with a tick or
   * a cross on each box. The answer key is what a tutor wants while choosing a
   * worksheet, not while looking at work a child has already done.
   */
  activityKey?: string | null
  onClose: () => void
}

export function BmdSkillbuilderPreviewModal({
  skillbuilderId,
  skillName,
  activityKey,
  onClose,
}: Props) {
  const [info, setInfo] = useState<BmdSkillbuilderPreviewResponse | null>(null)
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  // Tutors mostly want to see the answers; the blank version is one click away
  // for when they want to look at the worksheet as the student will get it.
  // Off when previewing assigned work: there the attempt is the point, and the
  // key would be the wrong thing to have on screen next to a student.
  const [showAnswers, setShowAnswers] = useState(!activityKey)

  const isOpen = skillbuilderId != null

  useEffect(() => {
    if (!isOpen) {
      setInfo(null)
      setPdfData(null)
      setNumPages(0)
      setError("")
      setShowAnswers(!activityKey)
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError("")

    getBmdSkillbuilderPreview(Number(skillbuilderId), undefined, activityKey)
      .then(async data => {
        if (cancelled) return
        setInfo(data)
        // Through the API, not straight from the asset store: see
        // getBmdSkillbuilderPdf. Fetched as bytes rather than handed to
        // <Document> as a URL so a failure is reported here, instead of
        // react-pdf silently showing nothing.
        // With an activity key the server decides: the student's marked
        // attempt if there is one, the blank worksheet if not.
        const bytes = await getBmdSkillbuilderPdf(
          Number(skillbuilderId),
          showAnswers,
          undefined,
          activityKey
        )
        if (!cancelled) setPdfData(bytes)
      })
      .catch(err => {
        console.error("Could not load the skill builder preview", err)
        if (!cancelled) setError("This Skill Builder could not be loaded.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, skillbuilderId, showAnswers, activityKey])

  const file = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData])

  // Present only when the preview was opened against an assigned activity AND
  // the student has submitted it.
  const attempt = info?.attempt || null

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader
        toggle={onClose}
        // The theme hides the default close button globally, so it is passed in.
        close={
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
          />
        }
      >
        <span className="font-size-15">{skillName || "Skill Builder"}</span>
      </ModalHeader>

      <ModalBody>
        {info ? (
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            {info.can_be_marked ? (
              <Badge className="bg-success font-size-11">
                Can be auto-marked
              </Badge>
            ) : (
              <Badge className="bg-danger font-size-11">
                Cannot be auto-marked
              </Badge>
            )}
            {info.can_be_marked ? (
              <span className="text-muted font-size-12">
                {info.total_questions} question
                {info.total_questions === 1 ? "" : "s"}, {info.total_fields}{" "}
                answer box
                {info.total_fields === 1 ? "" : "es"}
              </span>
            ) : (
              <span className="text-danger font-size-12">{info.message}</span>
            )}
            {attempt ? (
              <Badge
                className={
                  attempt.passed
                    ? "bg-success font-size-11"
                    : "bg-warning text-dark font-size-11"
                }
              >
                Student scored {attempt.score?.percent}%
                {attempt.pass_percent != null
                  ? ` (pass is ${attempt.pass_percent}%)`
                  : ""}
                {/* Under group marking the headline percentage is out of whole
                    questions, so the box tally is spelled out. A tutor counting
                    ticks on the page would otherwise not be able to reconcile
                    it. */}
                {attempt.score?.unit === "questions" && attempt.score?.per_box
                  ? ` - ${attempt.score.correct}/${attempt.score.total} questions, ${attempt.score.per_box.correct}/${attempt.score.per_box.total} boxes`
                  : ""}
              </Badge>
            ) : activityKey ? (
              <Badge className="bg-light text-dark font-size-11">
                Not attempted yet
              </Badge>
            ) : null}
            {info.misconception_type ? (
              <Badge className="bg-light text-dark font-size-11">
                {info.misconception_type === "RECENTLY_LEARNED"
                  ? "For: confusion with a recently learned skill"
                  : "For: lack of depth"}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <p className="text-center p-4 text-muted">Loading…</p>
        ) : null}
        {error ? (
          <p className="text-center p-4 text-danger fw-500">{error}</p>
        ) : null}

        {file ? (
          <div className="d-flex flex-column align-items-center">
            <Document
              file={file}
              onLoadSuccess={pdf => setNumPages(pdf.numPages)}
              onLoadError={err => {
                console.error("Could not render the skill builder PDF", err)
                setError("This Skill Builder could not be displayed.")
              }}
              loading={<p className="text-center p-4 text-muted">Loading…</p>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={`sb_preview_${i + 1}`}
                  pageNumber={i + 1}
                  width={700}
                  renderAnnotationLayer={true}
                  renderTextLayer={false}
                />
              ))}
            </Document>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter className="border-0">
        <span className="text-muted font-size-12 me-auto">
          {attempt
            ? "Showing what the student answered, marked."
            : showAnswers
              ? "Showing the answers. Nothing typed here is saved."
              : "Showing the blank worksheet, as the student gets it."}
        </span>
        {/* A marked attempt is not a view the toggle can switch away from:
            there is one record of what the student wrote. */}
        {attempt ? null : (
          <Button
            color="light"
            onClick={() => setShowAnswers(v => !v)}
            disabled={loading || !info?.can_be_marked}
          >
            {showAnswers ? "Show blank" : "Show answers"}
          </Button>
        )}
        <Button color="light" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default BmdSkillbuilderPreviewModal
